import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Signal,
  signal,
  SimpleChanges,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatRadioChange } from '@angular/material/radio';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import isEqual from 'fast-deep-equal';
import { produce } from 'immer';
import { omit, uniqWith, zipWith } from 'lodash-es';
import { combineLatest, delayWhen, filter, map, Observable, scan, Subject } from 'rxjs';
import { SetRequired } from 'type-fest';

import {
  CanvasElementDataProperties,
  CanvasElementPropertiesWithData,
  type ElementFormState,
  InputElementMetaUiFeatures,
  makeFontStyleStyles,
  OptionPickerDefaultValue,
  OptionsIconSettings,
  OptionsImageSettings,
  RuntimeContext,
  TileSelectorModes,
  toCssString,
} from '@blaze-shared/elements-domain';
import { getBackground } from '@blaze-shared/elements-domain';
import { selectItemByValue } from '@blaze-shared/schema-domain';
import {
  BlazeSchema,
  createArrayOfObjectsSchemaFromColumns,
  type DatasetRow,
  type ElementSchema,
  isDatasetLinkValue,
  SCHEMA_COLUMNS_LINK,
} from '@blaze-shared/schema-domain';
import { safeDetectChanges } from '@blaze-shared/shared-ui';
import { isNullish, isSelectOption, useEmitTracker, valueToBool } from '@blaze-shared/shared-util';

import { CanvasElementComponent } from '../../canvas-elements/element-component';
import { RowGenerationService } from '../../services/row-generation.service';
import {
  filterOptionsWithEmptyLabel,
  getFirstOption,
  isListSourceBound,
} from '../../utils/canvas-element/bindings.utils';
import { updateOptionsFields } from '../../utils/elements/element.utils';
import {
  CanvasElementBaseComponent,
  ColumnIdOrName,
  TriggerUpdatePayload,
} from '../__base/base.component';
import { AdditionalFieldDef, CardContentConfig } from '../checkbox/checkbox.model';

// TODO: test
@UntilDestroy()
@Component({
  selector: 'be-radio',
  templateUrl: './radio.component.html',
  styleUrls: ['./radio.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RadioComponent
  extends CanvasElementBaseComponent<'radio'>
  implements CanvasElementComponent<'radio'>, OnChanges, OnInit, OnDestroy
{
  private readonly rowGenerationService = inject(RowGenerationService);

  @Input() selected?: DatasetRow;
  @Input() optionsList?: DatasetRow[];
  @Input() formState?: ElementFormState;

  private readonly outputEmitTracker = useEmitTracker();
  private isSourceBound?: boolean;
  private selectedValue: DatasetRow | undefined;
  private isSelectionLinkValue = false;
  private isSelectionArray = false;
  private readonly dataSelected = this.getDataInput('selected');
  private readonly dataOptionsList = this.getDataInput('optionsList');

  protected readonly cardStyles$ = this.props$.pipe(
    map((props) => {
      const cardStyle = props?.style?.card;
      if (!cardStyle) {
        return undefined;
      }
      return {
        heading: {
          align: cardStyle.heading?.align,
        },
        subheading: {
          color: cardStyle.subheading?.color?.color1,
          fontWeight: cardStyle.subheading?.fontWeight,
          fontSize: cardStyle.subheading?.fontSize,
          fontStyle: cardStyle.subheading?.fontStyle,
          spaceAbove: cardStyle.subheading?.spaceAbove,
          align: cardStyle.subheading?.align,
        },
        description: {
          color: cardStyle.description?.color?.color1,
          fontSize: cardStyle.description?.fontSize,
          fontWeight: cardStyle.description?.fontWeight,
          spaceAbove: cardStyle.description?.spaceAbove,
          align: cardStyle.description?.align,
        },
        customField: {
          color: cardStyle.customField?.color?.color1,
          fontSize: cardStyle.customField?.fontSize,
          spaceAbove: cardStyle.customField?.spaceAbove,
          align: cardStyle.customField?.align,
        },
      };
    }),
  );
  protected readonly cardContentForOption!: Signal<(option: DatasetRow) => CardContentConfig>;

  readonly elementKeyRoot = 'b-r';
  elementKey!: string;

  labelEnabled!: boolean;
  labelText!: string;
  displayedOptions: DatasetRow[] = [];
  defaultValue?: OptionPickerDefaultValue;
  isReadOnly = false;
  triggerUpdate$ = new Subject<TriggerUpdatePayload>();

  styles!: Record<string, string>;
  inputWrapperStyles!: Record<string, string>;
  validationStyles!: Record<string, string>;
  tileStyle: Record<string, string>;
  tileStyles: Record<string, string>;
  labelStyles: Record<string, string>;
  subTextStyles: Record<string, string> | undefined;

  @ColumnIdOrName() labelField = 'label';
  @ColumnIdOrName() valueField = 'value';
  @ColumnIdOrName() imageField = 'imageSrc';
  @ColumnIdOrName() iconField = 'icon';
  @ColumnIdOrName() subheadingField = '';
  @ColumnIdOrName() descriptionField = '';

  mode: TileSelectorModes;
  imagePosition: 'left' | 'top' | 'bottom' | 'right' = 'left';
  layout = 'row';
  cardAlign: 'left' | 'right' | 'center' | undefined;
  selectedOptions: DatasetRow[] = [];
  imageSettings: OptionsImageSettings | undefined;
  iconSettings: OptionsIconSettings | undefined;
  showCheckIcon: boolean | undefined;

  additionalFieldDefs = signal<AdditionalFieldDef[]>([]);
  visibleAdditionalFieldDefs$!: Observable<
    ((option: DatasetRow) => AdditionalFieldDef[]) | undefined
  >;

  get hasValidationError(): boolean {
    return !!this.formState?.errors?.required || !!this.formState?.errors?.invalid;
  }

  get showCheckmark(): boolean {
    return !!this.showCheckIcon && (this.mode === 'box' || this.mode === 'card');
  }

  get boxSubTextField(): string {
    return this.bindingResults?.['optionsList'] ? this.subheadingField : 'subheading';
  }

  get showIcon(): boolean {
    return this.displayedOptions.some((option) => !!option.icon);
  }

  get showImage(): boolean {
    const ab = this.bindingResults?.['optionsList'];
    return !ab || (!!ab && !!this.imageField);
  }

  constructor() {
    super();

    combineLatest({
      selectedSchema: this.dataSelected.schema$,
      optionsListSchema: this.dataOptionsList.schema$,
    })
      .pipe(untilDestroyed(this))
      .subscribe(({ selectedSchema, optionsListSchema }) => {
        this.updateSchemas(selectedSchema, optionsListSchema);
      });

    // Trigger options list and selection update (includes the logic of fetching the selected value when dynamic search is on)
    this.triggerUpdate$
      .pipe(
        // Reuse the previous empty row if not provided
        scan(
          (acc, updateData) => {
            return {
              emptyRow: updateData.emptyRow ?? acc.emptyRow, // default to previous value
              optionsListOnly: updateData.optionsListOnly ?? false, // always default to false
            };
          },
          { emptyRow: undefined, optionsListOnly: false } as TriggerUpdatePayload,
        ),
        // Empty row is needed to proceed
        filter(
          (updatePayload): updatePayload is SetRequired<TriggerUpdatePayload, 'emptyRow'> =>
            !!updatePayload?.emptyRow,
        ),
        delayWhen(() => this.getElementEventSchemaSet('change')),
        untilDestroyed(this),
      )
      .subscribe(({ emptyRow, optionsListOnly }) => {
        this.updateOptionsList();
        if (optionsListOnly !== true) {
          this.updateSelection(emptyRow);
        }
        safeDetectChanges(this.cdr);
      });

    this.initMarginHandler((props) => props?.style?.general?.offset?.margin);

    this.visibleAdditionalFieldDefs$ = combineLatest({
      context: this.runtimeContext$,
      customFields: toObservable(this.additionalFieldDefs).pipe(
        map((fields) => fields.filter((field) => field.fieldKey)),
      ),
    }).pipe(
      map(({ context, customFields }) => {
        if (!customFields) {
          return undefined;
        }

        // Role filter applied once — roles don't depend on individual option data
        const roleFiltered = customFields.some((field) => !!field.visible?.accessRoles?.length)
          ? customFields.filter(
              (field) =>
                !field.visible?.accessRoles?.length ||
                this.userAccessRolesService.isCurrentUserInAccessRoles(field.visible.accessRoles),
            )
          : customFields;

        // If no custom fields are conditionally visible - show the same custom fields for all options
        if (!roleFiltered.some((field) => !!field.visibilityExpression)) {
          return (_option: DatasetRow): AdditionalFieldDef[] => roleFiltered;
        }

        // Normalization is done here for improved performance - the normalized context is shared across all options
        const normalizedContext = this.normalizeLinkedParams(context);

        return (option: DatasetRow): AdditionalFieldDef[] => {
          const optionContext = {
            ...normalizedContext,
            output: this.normalizeLinkedParams(option as RuntimeContext),
          };
          return roleFiltered.filter(
            (field) =>
              !field.visibilityExpression ||
              !!this.evaluateExpression(field.visibilityExpression, optionContext, true),
          );
        };
      }),
    );

    const visibleDefsSignal = toSignal(this.visibleAdditionalFieldDefs$, {
      initialValue: undefined as ((option: DatasetRow) => AdditionalFieldDef[]) | undefined,
    });
    this.cardContentForOption = computed(
      () =>
        (option: DatasetRow): CardContentConfig => ({
          subheading: option[this.subheadingField],
          description: option[this.descriptionField],
          customFields: visibleDefsSignal()?.(option)?.map((def) => ({
            ...def,
            value: option[def.fieldKey],
          })),
        }),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['props']) {
      if (!this.elementKey) {
        this.elementKey = `${this.elementKeyRoot}-${this.elementId}`;
      }

      const {
        title,
        radio,
        data: contentData,
        image: imageSettings,
        icon: iconSettings,
        mode,
        showCheckIcon,
      } = this.props?.content ?? {};

      this.labelEnabled = title?.enabled ?? true;
      this.defaultValue = contentData?.defaultValue;
      this.imageSettings = imageSettings;
      this.iconSettings = iconSettings;
      this.showCheckIcon = showCheckIcon;

      this.mode = mode ?? 'text';
      this.layout = radio?.layout ?? 'row';

      const { data } = (this.props as CanvasElementPropertiesWithData) || {};
      if (this.bindingResults?.['optionsList'] && data?.length) {
        const binding = data.find((b) => b.key === 'optionsList');
        if (binding && isSelectOption(binding.bindings)) {
          updateOptionsFields(this, binding.bindings.value as string, binding.bindings.label);
          this.imageField = binding.bindings.imageSrc;
          if (binding.bindings.icon) {
            this.iconField = binding.bindings.icon;
          }
          this.applyExtraBindingFields(binding.bindings as Record<string, unknown>);
        } else {
          updateOptionsFields(this);
        }
      } else {
        this.optionsList = radio?.values.map((value) => ({ value, label: value })) || [];
        if (this.mode === 'image' || this.mode === 'box') {
          this.optionsList = zipWith(
            this.optionsList ?? [],
            radio?.images ?? [],
            radio?.subheading ?? [],
            (option, image, subheading) => ({
              ...option,
              imageSrc: image?.src,
              icon: image?.icon,
              ...(this.mode === 'box' && subheading ? { subheading } : {}),
            }),
          );
        } else if (this.mode === 'card' && !this.bindingResults?.['optionsList']) {
          this.optionsList = zipWith(
            this.optionsList ?? [],
            radio?.images ?? [],
            radio?.subheading ?? [],
            radio?.description ?? [],
            (option, image, subheading, description) => ({
              ...option,
              imageSrc: image?.src,
              subheading,
              description,
            }),
          );
        }
        updateOptionsFields(this);
      }

      if (this.mode === 'image') {
        this.imagePosition = imageSettings?.position ?? 'left';
      } else if (this.mode === 'card') {
        this.imagePosition = imageSettings?.position ?? 'right';
      } else {
        this.imagePosition = iconSettings?.position ?? 'left';
      }

      this.cardAlign = radio?.cardAlign;
      this.assignAdditionalFields();

      if (!this.bindingResults?.['optionsList'] && !this.bindingResults?.['selected']) {
        this.updateSchemas(undefined, undefined);
      }
      this.updateStyles();
    }

    if (changes?.['selected']) {
      this.processSelection();
    }

    const emptyRow = this.getEmptyRow(!!changes?.['optionsList']);
    if (changes?.['props'] || changes?.['optionsList'] || changes?.['selected']) {
      this.triggerUpdate$.next({ emptyRow });
    }

    if (changes?.['props']) {
      this.isSourceBound = isListSourceBound(this.props?.data as CanvasElementDataProperties[]);
    }

    this.selectedOptions = this.selected ? [this.selected] : [];
  }

  ngOnInit(): void {
    this.getElementUiFeatures<InputElementMetaUiFeatures>()
      .pipe(untilDestroyed(this))
      .subscribe((features) => {
        this.labelText = (features.title ?? '') + this.getRequiredSuffix(features);

        this.isReadOnly = features.isReadOnly || this.props?.content?.radio?.readonly || false;
        safeDetectChanges(this.cdr);
      });
    this.stylesCleanup.register(this.elementKey);
  }

  ngOnDestroy(): void {
    this.stylesCleanup.dispose(this.elementKey);
  }

  onSelectionChanged(event: MatRadioChange): void {
    this.handleSelect(event.value);
  }

  handleSelect(option: DatasetRow): void {
    this.selectedValue = selectItemByValue(this.optionsList, option, this.valueField);
    if (this.selectedValue) {
      this.updateOutput(this.selectedValue);
    }
    this.triggerUpdate$.next({ optionsListOnly: true });
  }

  reset(): void {
    this.outputEmitTracker.reset();
    this.triggerUpdate$.next({ emptyRow: this.getEmptyRow(true) });
    this.cdr.detectChanges();
  }

  selectOption(options: DatasetRow[] | undefined): void {
    if (!options || this.isReadOnly) {
      return;
    }
    const [first] = options;
    if (!first) {
      return;
    }
    this.handleSelect(first);
  }

  private applyExtraBindingFields(bindings: Record<string, unknown>): void {
    this.subheadingField = typeof bindings['subheading'] === 'string' ? bindings['subheading'] : '';
    this.descriptionField =
      typeof bindings['description'] === 'string' ? bindings['description'] : '';
  }

  private assignAdditionalFields(): void {
    const fields = this.props?.content?.customFields ?? [];
    this.additionalFieldDefs.set(
      fields.map((f: any) => ({
        ...f,
        label: f.label ?? '',
        fieldKey: this.getColumnName(f.column) ?? f.column ?? '',
        format: f.displayFormat ?? '',
        labelBehavior: f.labelBehavior ?? 'left',
      })),
    );
  }

  private processSelection(): void {
    if (Array.isArray(this.selected)) {
      this.isSelectionArray = true;
      if (this.selected.length) {
        this.selected = this.selected[0];
      }
    }

    if (isDatasetLinkValue(this.selected)) {
      this.isSelectionLinkValue = true;
      this.selected = this.selected.rowId as any;
    }

    if (typeof this.selected === 'boolean' && !this.bindingResults?.['optionsList']) {
      this.selectedValue = this.optionsList?.find(
        (opt) =>
          typeof this.selected === 'boolean' && // ts can't see the check 2 lines above
          valueToBool(opt[this.valueField]) === this.selected,
      )?.[this.valueField];
    } else {
      this.selectedValue = this.selected;
    }
  }

  private updateOptionsList(): void {
    this.displayedOptions = [...(this.optionsList || [])].filter(
      filterOptionsWithEmptyLabel(this.labelField),
    );

    // Keep unique value-label pairs (value can be a nested object)
    this.displayedOptions = uniqWith(
      this.displayedOptions,
      (a: DatasetRow, b: DatasetRow) =>
        (isEqual(a[this.valueField], b[this.valueField]) &&
          a[this.labelField] === b[this.labelField]) ||
        (isDatasetLinkValue(a[this.labelField]) &&
          isDatasetLinkValue(b[this.labelField]) &&
          a[this.labelField].rowId === b[this.labelField].rowId),
    );
  }

  private updateSelection(emptyRow: DatasetRow): void {
    const { radio } = this.props?.content || {};

    const wasSourceBound = this.isSourceBound;
    const isSourceBound = isListSourceBound(this.props?.data as CanvasElementDataProperties[]);
    if (wasSourceBound !== undefined && wasSourceBound !== isSourceBound) {
      this.selectedValue = undefined;
    }

    let selected = null;
    if (
      this.optionsList?.length &&
      !isNullish(this.selectedValue) &&
      !isEqual(this.selectedValue, emptyRow)
    ) {
      selected = selectItemByValue(this.optionsList, this.selectedValue, this.valueField);
    } else {
      switch (this.defaultValue) {
        case 'first':
          selected = getFirstOption(this.optionsList, this.valueField);
          break;
        case 'fixedValue':
          if (radio?.preselect) {
            selected = selectItemByValue(this.optionsList, radio?.preselect, this.valueField);
          }
          break;
      }
    }
    if (this.optionsList) {
      this.updateOutput(selected);
    }
  }

  private getEmptyRow(bustCache = false): DatasetRow {
    let row: DatasetRow = {
      [this.labelField]: '',
      [this.valueField]: null,
      [this.imageField]: '',
    };
    if (isListSourceBound(this.props?.data as CanvasElementDataProperties[])) {
      const cachedRow = this.rowGenerationService.getCachedNewRow(
        this.elementId,
        this.props?.data as CanvasElementDataProperties[],
        bustCache,
      );
      if (typeof cachedRow === 'object' && this.valueField in cachedRow) {
        row = cachedRow;
      }
    }
    return row;
  }

  private updateSchemas(
    selectedSchema: BlazeSchema | undefined,
    optionsListSchema: BlazeSchema | undefined,
  ): void {
    // If value is selected but options list data is bound, the schema should return the schema of the bound data source.
    if (selectedSchema && !optionsListSchema) {
      if (this.dataSelected.column?.columnType === 'Boolean') {
        this.setDefaultEventSchema('change');
      } else {
        this.setEventSchemaFromDataInput('change', this.dataSelected);
      }
    } else if (optionsListSchema && this.bindingResults?.['optionsList']) {
      if (this.bindingResults?.['selected'] && !selectedSchema) {
        return;
      }

      let eventSchema = this.createElementSchema('change', undefined, optionsListSchema);

      if (this.dataSelected.column?.columnType === 'Link') {
        this.isSelectionLinkValue = true;
        eventSchema = produce(eventSchema, (draft) => {
          draft?.columns.push(...SCHEMA_COLUMNS_LINK);
        });
      } else if (
        this.dataSelected.column?.multipleValues ||
        this.dataSelected.column?.dataType === 'Array'
      ) {
        this.isSelectionArray = true;
        const optionsArraySchema = createArrayOfObjectsSchemaFromColumns(
          optionsListSchema.name,
          optionsListSchema.columns,
        );
        eventSchema = this.createElementSchema('change', undefined, optionsArraySchema);
      }
      this.setDefaultEventSchema('change', eventSchema);
    } else {
      this.setDefaultEventSchema('change');
    }
  }

  private updateOutput(data: DatasetRow): void {
    if (!data) {
      data = this.getEmptyRow();
    }
    const hadNullValue = this.selected === null;
    this.selected = data;
    if (this.isSelectionLinkValue) {
      data = {
        ...data,
        rowId: data[this.valueField] ?? null,
        display: data[this.labelField] ?? null,
      };
    }
    if (this.isSelectionArray) {
      data = [data];
    }
    const schema = this.getEventSchema('change');
    const isInitialData = !this.outputEmitTracker.hasChanges();
    if (
      (!schema || !this.outputEmitTracker.shouldEmit(data)) &&
      !(data && hadNullValue) // allow emit when selected value was null, but now has a value from defaults
    ) {
      return;
    }

    const cleanData = this.adjustWithOutputSchema(data, schema);
    this.output.emit({
      eventType: 'change',
      data: cleanData,
      isInitialData,
      schema,
    });
  }

  private adjustWithOutputSchema(
    data: DatasetRow | DatasetRow[],
    schema: ElementSchema | undefined,
  ): DatasetRow {
    if (Array.isArray(data)) {
      return data.map((d) => this.adjustWithOutputSchema(d, schema));
    }
    if (!schema) {
      return data;
    }

    const defaultSchema = this.getDefaultEventSchema('change');
    const isDefaultSchema = schema.columns === defaultSchema?.columns;
    if (!isDefaultSchema) {
      return data;
    }
    return omit(data, [this.imageField]);
  }

  private updateStyles(): void {
    const { style, content } = this.props || {};
    const { title, radio: radioContent } = content || {};
    const { general, radio, labelBlock } = style || {};
    if (!style || !title || !general || !radio || !labelBlock) {
      return;
    }

    const radioBorderBackground = getBackground(radio.border);
    const styles: Record<string, string> = {
      '--radio-label-color': radio.label.color1,
      '--radio-font-size': style.fontSize,
      '--radio-font-family': style.fontFamily,
      '--radio-line-height': style.lineHeight,
      '--radio-accent': general.accent.color1,
      '--radio-card-checkmark-border':
        radioBorderBackground === 'none' ? 'transparent' : radioBorderBackground,
      '--radio-card-checkmark-tick': radio.label.color1,
      '--radio-chip-fill': getBackground(radio.fill),
      '--tile-padding': radio.padding ?? '16px',
      '--tile-gap': radio.gap ?? '8px',
    };
    if (radio.tileRadius) {
      styles['--tile-radius'] = radio.tileRadius;
    }
    if (radio.checkmarkSize) {
      styles['--checkmark-size'] = radio.checkmarkSize;
    }

    const inputWrapperStyles: Record<string, string> = {};
    const validationStyles: Record<string, string> = {};
    const tileStyle: Record<string, string> = {};
    const tileStyles: Record<string, string> = {};
    const labelStyles: Record<string, string> = {};

    labelStyles['fontSize'] = style.fontSize;
    labelStyles['fontFamily'] = style.fontFamily;
    labelStyles['fontWeight'] = style.fontWeight;
    labelStyles['color'] = radio.label.color1;

    if (style.radio.fill) {
      tileStyles['background'] = getBackground(style.radio.fill);
    }
    if (style.radio.border) {
      tileStyles['borderColor'] =
        radio.border.selectorType === 'none' ? 'transparent' : radio.border.color1;
    }

    const fontStyles = makeFontStyleStyles(style.fontStyle);
    tileStyle['fontSize'] = style.fontSize;
    tileStyle['fontFamily'] = style.fontFamily;
    tileStyle['fontWeight'] = style.fontWeight;
    tileStyle['color'] = style.radio.label.color1;

    if (style.radio.fill) {
      tileStyle['background'] = getBackground(style.radio.fill);
    }
    if (style.radio.border) {
      tileStyle['borderColor'] =
        radio.border.selectorType === 'none' ? 'transparent' : radio.border.color1;
    }

    Object.assign(tileStyle, fontStyles);
    Object.assign(labelStyles, fontStyles);

    if (title.enabled !== false) {
      if (title.position === 'top') {
        styles['flexDirection'] = `column`;
      } else {
        validationStyles['marginLeft'] = title.width;
        validationStyles['paddingLeft'] = '8px';
        inputWrapperStyles['flexBasis'] = `${100 - parseInt(title.width, 10)}%`;
      }
    }

    if (radioContent?.layout === 'column') {
      inputWrapperStyles['flexDirection'] = 'column';
      inputWrapperStyles['alignItems'] = 'flex-start';
    }

    this.styles = styles;
    this.inputWrapperStyles = inputWrapperStyles;
    this.validationStyles = validationStyles;
    this.tileStyle = tileStyle;
    this.tileStyles = tileStyles;
    this.labelStyles = labelStyles;

    const subText = style.box?.subText;
    this.subTextStyles = subText
      ? {
          color: subText.color?.color1 ?? '',
          fontFamily: subText.fontFamily ?? '',
          fontSize: subText.fontSize ?? '',
          lineHeight: subText.lineHeight ?? '',
        }
      : undefined;

    const radioBorder = radio.border.selectorType === 'none' ? 'transparent' : radio.border.color1;
    this.stylesCleanup.update(
      this.elementKey,
      `
      .${this.elementKey} .mdc-label {
        white-space: normal;
        color: ${radio.label.color1};
        font-size: ${style.fontSize};
        font-family: ${style.fontFamily};
        line-height: ${style.lineHeight};
        ${style.fontStyle ? toCssString(makeFontStyleStyles(style.fontStyle)) : ''}
      }
      .${this.elementKey}.mat-mdc-radio-button {
        --mat-radio-bg: ${radio.fill.color1};
        --mat-radio-unselected-icon-color: ${radioBorder};
        --mat-radio-unselected-pressed-icon-color: ${radioBorder};
        --mat-radio-unselected-hover-icon-color: ${radioBorder};
        --mat-radio-selected-hover-icon-color: ${general.accent.color1};
        --mat-radio-selected-pressed-icon-color: ${general.accent.color1};
        --mat-radio-selected-icon-color: ${general.accent.color1};
      }
    `,
    );
  }
}
